/**
 * Relative labels for last-verified / updated timestamps.
 * Falls back to a compact date once the value is more than a year old, so
 * the vault doesn't wall off with unbounded numbers ("847d ago").
 */
export function relativeTimeLabel(iso: string, now: Date = new Date()): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) {
        return '—';
    }
    const seconds = Math.max(0, Math.round((now.getTime() - then) / 1000));
    if (seconds < 45) {
        return 'just now';
    }
    if (seconds < 90) {
        return '1m ago';
    }
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) {
        return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 24) {
        return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 30) {
        return `${days}d ago`;
    }
    const months = Math.round(days / 30);
    if (months < 12) {
        return `${months}mo ago`;
    }
    const years = Math.round(days / 365);
    return `${years}y ago`;
}

export function absoluteTimeLabel(iso: string): string {
    const asDate = new Date(iso);
    if (Number.isNaN(asDate.getTime())) {
        return iso;
    }
    return `${asDate.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}

/**
 * Formats the same instant in a specific IANA timezone, e.g. "2026-07-22 09:41
 * EDT". Used to render org-local time alongside UTC so vault readers don't
 * have to convert dates in their head for organization-scoped notes.
 */
export function absoluteTimeInTimezoneLabel(
    iso: string,
    timezone: string,
): string {
    const asDate = new Date(iso);
    if (Number.isNaN(asDate.getTime())) {
        return iso;
    }
    // Intl format that dependable Chrome/Firefox/Safari all agree on:
    // `2026-07-22, 09:41 AM EDT`. Reformatted to sort-friendly output.
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short',
    }).formatToParts(asDate);
    const lookup: Record<string, string> = {};
    for (const part of parts) {
        lookup[part.type] = part.value;
    }
    const date = `${lookup['year'] ?? ''}-${lookup['month'] ?? ''}-${lookup['day'] ?? ''}`;
    const time = `${lookup['hour'] ?? ''}:${lookup['minute'] ?? ''}`;
    const zone = lookup['timeZoneName'] ?? timezone;
    return `${date} ${time} ${zone}`;
}
